import React, {
  FC, memo, useCallback, useContext, useState,
} from 'react';
import {
  makeStyles, Button, Tooltip, mergeClasses, TooltipProps, tokens, shorthands,
} from '@fluentui/react-components';
import { Copy16Regular, Copy16Filled, bundleIcon } from '@fluentui/react-icons';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';

const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular);

const useCopyButtonStyles = makeStyles({
  copyBtn: {
    color: tokens.colorNeutralForeground3,
    '&.fui-Icon-filled': {
      color: tokens.colorNeutralForeground2BrandSelected,
    },
  },
  hiddenA11yAnnounceArea: {
    position: 'absolute',
    width: '1px',
    clip: 'rect(0 0 0 0)',
    height: '1px',
    ...shorthands.margin(0),
    ...shorthands.overflow('hidden'),
    ...shorthands.padding(0),
    ...shorthands.border('0'),
  },
});

interface ICopyButtonProps {
  messageText: string;
  ariaHiddenWhileLoading: boolean;
}

const COPY_BTN_TIP = _TL_('Copy');
const COPIED_TIP = _TL_('Copied!');

export const CopyButton: FC<ICopyButtonProps> = memo(({ messageText, ariaHiddenWhileLoading }) => {
  const styles = useCopyButtonStyles();
  const i18n = useContext(I18nContext) as I18nModel;
  const [tooltipContent, setTooltipContent] = useState(i18n.getString(COPY_BTN_TIP));
  const copyMessage = useCallback(() => {
    navigator.clipboard.writeText(messageText)
      .then(() => {
        setTooltipContent(i18n.getString(COPIED_TIP));

        const a11yAnnounceArea = document.getElementById('a11yAnnounceArea');
        if (a11yAnnounceArea) {
          a11yAnnounceArea.textContent = i18n.getString(COPIED_TIP);
          a11yAnnounceArea.textContent = '';
        }
      });
  }, [i18n, messageText]);
  const onVisibleChange = useCallback<NonNullable<TooltipProps['onVisibleChange']>>((_evt, data) => {
    if (!data.visible) {
      setTooltipContent(i18n.getString(COPY_BTN_TIP));
    }
  }, [i18n]);

  return (
    <React.Fragment>
      <Tooltip
        content={tooltipContent}
        relationship="label"
        onVisibleChange={onVisibleChange}
      >
        <Button
          className={mergeClasses('copy-btn')}
          icon={<CopyIcon className={styles.copyBtn} />}
          appearance="transparent"
          size="small"
          onClick={copyMessage}
          aria-label={i18n.getString(COPY_BTN_TIP)}
          aria-hidden={ariaHiddenWhileLoading}
          tabIndex={ariaHiddenWhileLoading ? -1 : 0}
        />
      </Tooltip>
      {/* for a11y announce do not remove the div below */}
      <div
        id="a11yAnnounceArea"
        aria-live="polite"
        aria-atomic="true"
        className={styles.hiddenA11yAnnounceArea}
      />
    </React.Fragment>
  );
});
